import {
  Badge,
  Button,
  Checkbox,
  cn,
  Combobox,
  type ComboboxOption,
  FormField,
  FormFieldError,
  FormFieldHint,
  FormFieldLabel,
  RadioCardGroup,
  RadioCardItem,
} from '@eureca/ui';
import { XIcon } from '@eureca/ui/icons';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { BlockedEditTooltip } from '@/features/jobs/job-shared/BlockedEditTooltip';
import { JobHeaderLogo } from '@/features/jobs/job-shared/JobHeaderLogo';

export type WorkModel = 'hibrido' | 'presencial' | 'remoto';

// EU-497 — Turno agora inclui `integral` (múltipla escolha).
export type ShiftType = 'matinal' | 'vespertino' | 'noturno' | 'integral';

export type WeeklyHours = '15' | '20' | '30' | '40' | '44';

export type YesNo = 'sim' | 'nao' | '';

export type RelocationScope = 'dentro-do-brasil' | 'fora-do-brasil';

/** Um tipo do catálogo, no vocabulário que a API de vagas entende. */
export interface JobContractTypeOption {
  value: string;
  label: string;
}

export interface JobEditConditionsValues {
  /**
   * EU-709 D-02 — token PT-BR do `Tipo de vaga`. É o campo que declara que a
   * vaga é um Banco de talentos, e é dele que sai a obrigatoriedade de todas
   * as seções. Por isso `Condições de trabalho` nunca fica opcional (RN-10).
   */
  contractType: string;
  // EU-497 — Condições de trabalho múltipla escolha: Modelo, Turno e Carga
  // horária passam a ser arrays (checkbox). Os valores são tokens (strings)
  // do Core; o container converte a Carga (`'30'`) para número no payload.
  workModels: string[];
  /** Juliana QA bug — EU-160. Multi-select arrays. */
  requirementStates: string[];
  requirementCities: string[];
  /** EU-497 RN-04 — cidades do "Local de trabalho", como "Cidade - UF". */
  workplaceCities: string[];
  relocationAvailability: YesNo;
  relocationScope: RelocationScope[];
  travelAvailability: YesNo;
  travelScope: RelocationScope[];
  shifts: string[];
  weeklyHoursList: string[];
}

export interface JobEditConditionsPageProps {
  jobName: string;
  companyLogoUrl?: string | null;
  companyName?: string | null;
  values: JobEditConditionsValues;
  errors: Record<string, string>;
  isSubmitting: boolean;
  /** EU-569 — vaga Encerrada / Cancelada: modo somente visualização. */
  readOnly?: boolean;
  /** EU-709 — os oito tipos do catálogo (RN-01), já ordenados pelo rótulo. */
  contractTypeOptions: readonly JobContractTypeOption[];
  /**
   * EU-709 RN-11 / RN-20 — campos que a API marcou como opcionais nesta seção.
   * Num Banco de talentos é todo mundo menos o `Tipo de vaga`. A tela apenas
   * reflete: não existe lista fixa de campos opcionais no cliente.
   */
  optionalFields: readonly string[];
  stateOptions: ComboboxOption[];
  cityOptions: ComboboxOption[];
  // EU-497 RN-04 — "Local de trabalho" é um AUTOCOMPLETE único de cidade (busca
  // nacional por nome; opções "Cidade - UF"). Sem seletor de estado. As opções
  // vêm do servidor conforme o consultor digita (`onWorkplaceSearchChange`).
  workplaceCityOptions: ComboboxOption[];
  workplaceSearchLoading: boolean;
  onWorkplaceSearchChange: (query: string) => void;
  onFieldChange: <K extends keyof JobEditConditionsValues>(
    field: K,
    value: JobEditConditionsValues[K]
  ) => void;
  onBack: () => void;
  onSubmit: () => void;
}

const WORK_MODEL_OPTIONS: readonly { value: WorkModel; label: string }[] = [
  { value: 'hibrido', label: 'Híbrido' },
  { value: 'presencial', label: 'Presencial' },
  { value: 'remoto', label: 'Remoto' },
];

const YES_NO_OPTIONS: readonly { value: 'sim' | 'nao'; label: string }[] = [
  { value: 'sim', label: 'Sim' },
  { value: 'nao', label: 'Não' },
];

const SCOPE_OPTIONS: readonly { value: RelocationScope; label: string }[] = [
  { value: 'dentro-do-brasil', label: 'Dentro do Brasil' },
  { value: 'fora-do-brasil', label: 'Fora do Brasil' },
];

const SHIFT_OPTIONS: readonly { value: ShiftType; label: string }[] = [
  { value: 'matinal', label: 'Matinal' },
  { value: 'vespertino', label: 'Vespertino' },
  { value: 'noturno', label: 'Noturno' },
  // EU-497 — nova opção Integral.
  { value: 'integral', label: 'Integral' },
];

const WEEKLY_HOURS_OPTIONS: readonly { value: WeeklyHours; label: string }[] = [
  { value: '15', label: '15 horas' },
  { value: '20', label: '20 horas' },
  { value: '30', label: '30 horas' },
  { value: '40', label: '40 horas' },
  { value: '44', label: '44 horas' },
];

/**
 * EU-709 RN-11 — a mesma frase que o Figma usa nos campos que deixam de ser
 * exigidos. Já era o texto dos campos de localidade; agora ela é atribuída
 * pelo contrato da API, não escrita campo a campo.
 */
const OPTIONAL_FIELD_HINT = 'Esse campo é opcional';

export function JobEditConditionsPage({
  jobName,
  companyLogoUrl,
  companyName,
  values,
  errors,
  isSubmitting,
  readOnly = false,
  contractTypeOptions,
  optionalFields,
  stateOptions,
  cityOptions,
  workplaceCityOptions,
  workplaceSearchLoading,
  onWorkplaceSearchChange,
  onFieldChange,
  onBack,
  onSubmit,
}: JobEditConditionsPageProps) {
  const isOptional = (field: string) => optionalFields.includes(field);
  // EU-497 — múltipla escolha; sem special-case de Remoto. Os campos de
  // localidade ficam SEMPRE visíveis (RN-04: Local de trabalho independente
  // dos requisitos). O card do candidato mostra "Brasil" quando não há
  // localidade (RN-05, tratado no App Candidato).
  const showRelocationScope = values.relocationAvailability === 'sim';
  const showTravelScope = values.travelAvailability === 'sim';

  const requirementCityDisabled = values.requirementStates.length === 0;

  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="jobs" />

      <main className="flex flex-1 flex-col items-center gap-8 bg-card px-4 pt-8 pb-16 md:px-8">
        <form
          className="contents"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <header className="flex w-full max-w-[630px] flex-col gap-2">
            <div className="flex flex-row items-center gap-2">
              <JobHeaderLogo logoUrl={companyLogoUrl} companyName={companyName} />
              <h1 className="text-3xl font-medium leading-none tracking-tight text-foreground md:text-4xl">
                {jobName}
              </h1>
            </div>
            <p className="text-xl font-medium leading-none tracking-tight text-accent-foreground md:text-2xl">
              Condições de trabalho
            </p>
          </header>

          <div className="flex w-full max-w-[630px] flex-1 flex-col gap-5">
            {/* EU-569 — fieldset nativo desabilita os controles em modo
                visualização (checkboxes, radios, comboboxes, chips). */}
            <fieldset
              disabled={readOnly}
              className="contents"
              data-testid={readOnly ? 'job-edit-readonly-fieldset' : undefined}
            >
              {/* EU-709 D-02 — `Tipo de vaga`: primeiro campo da seção (Figma),
                  seleção única, ÚNICO obrigatório num Banco de talentos
                  (RN-11). É ele que a RN-10 usa para explicar por que esta
                  seção nunca recebe a tag `Opcional`. */}
              <BlockedEditTooltip enabled={readOnly}>
                <FormField error={Boolean(errors.contractType)}>
                  <FormFieldLabel className="text-foreground">Tipo de vaga</FormFieldLabel>
                  <RadioCardGroup
                    value={values.contractType || undefined}
                    onValueChange={(value) => onFieldChange('contractType', value)}
                    className="flex flex-row flex-wrap gap-3"
                    data-testid="job-conditions-contract-type"
                  >
                    {contractTypeOptions.map((option) => (
                      <RadioCardItem
                        key={option.value}
                        value={option.value}
                        className="group px-4 py-4"
                        data-testid={`job-conditions-contract-${option.value}`}
                      >
                        {option.label}
                      </RadioCardItem>
                    ))}
                  </RadioCardGroup>
                  <FormFieldError className="text-sm leading-5 text-muted-foreground">
                    {errors.contractType}
                  </FormFieldError>
                </FormField>
              </BlockedEditTooltip>

              {/* EU-497 — Modelo de trabalho múltipla escolha (RN-01). */}
              <BlockedEditTooltip enabled={readOnly}>
                <FormField error={Boolean(errors.workModels)}>
                  <FormFieldLabel className="text-foreground">Modelo de trabalho</FormFieldLabel>
                  <FormFieldHint>
                    {isOptional('workModels')
                      ? OPTIONAL_FIELD_HINT
                      : 'Selecione um ou mais modelos'}
                  </FormFieldHint>
                  <CheckboxPillGroup
                    options={WORK_MODEL_OPTIONS}
                    selected={values.workModels}
                    onToggle={(value) =>
                      onFieldChange('workModels', toggleValue(values.workModels, value))
                    }
                    testIdPrefix="job-conditions-work-model"
                  />
                  <FormFieldError className="text-sm leading-5 text-muted-foreground">
                    {errors.workModels}
                  </FormFieldError>
                </FormField>
              </BlockedEditTooltip>

              {/* EU-497 (RN-04) — localidade SEMPRE visível (sem hide por Remoto). */}
              <BlockedEditTooltip enabled={readOnly}>
                <div className="flex flex-col gap-5">
                  <FormField error={Boolean(errors.requirementStates)}>
                    <FormFieldLabel className="text-foreground">
                      Requisito de localidade — Estados
                    </FormFieldLabel>
                    <FormFieldHint>Esse campo é opcional</FormFieldHint>
                    <MultiSelectComboboxWithChips
                      options={stateOptions}
                      selected={values.requirementStates}
                      onChange={(next) => onFieldChange('requirementStates', next)}
                      placeholder="Selecione um ou mais estados"
                      searchPlaceholder="Busque por estados"
                      emptyMessage="Nenhum estado encontrado"
                      testId="job-conditions-requirement-states"
                      error={Boolean(errors.requirementStates)}
                    />
                    <FormFieldError className="text-sm leading-5 text-muted-foreground">
                      {errors.requirementStates}
                    </FormFieldError>
                  </FormField>

                  <FormField error={Boolean(errors.requirementCities)}>
                    <FormFieldLabel
                      className={cn('text-foreground', requirementCityDisabled && 'opacity-50')}
                    >
                      Requisito de localidade — Cidades
                    </FormFieldLabel>
                    <FormFieldHint>
                      {requirementCityDisabled
                        ? 'Selecione ao menos um estado primeiro'
                        : 'Esse campo é opcional'}
                    </FormFieldHint>
                    <MultiSelectComboboxWithChips
                      options={cityOptions}
                      selected={values.requirementCities}
                      onChange={(next) => onFieldChange('requirementCities', next)}
                      placeholder="Selecione uma ou mais cidades"
                      searchPlaceholder="Busque por cidades"
                      emptyMessage="Nenhuma cidade encontrada"
                      testId="job-conditions-requirement-cities"
                      error={Boolean(errors.requirementCities)}
                      disabled={requirementCityDisabled}
                    />
                    <FormFieldError className="text-sm leading-5 text-muted-foreground">
                      {errors.requirementCities}
                    </FormFieldError>
                  </FormField>

                  {/* EU-497 (RN-04) — Local de trabalho: AUTOCOMPLETE único de
                  cidade (busca nacional por nome, opções "Cidade - UF"). Sem
                  seletor de estado; o consultor digita e escolhe. */}
                  <FormField error={Boolean(errors.workplaceCities)}>
                    <FormFieldLabel className="text-foreground">Local de trabalho</FormFieldLabel>
                    <FormFieldHint>
                      {isOptional('workplaceCities')
                        ? OPTIONAL_FIELD_HINT
                        : 'Busque a cidade pelo nome'}
                    </FormFieldHint>
                    <MultiSelectComboboxWithChips
                      options={workplaceCityOptions}
                      selected={values.workplaceCities}
                      onChange={(next) => onFieldChange('workplaceCities', next)}
                      onSearchChange={onWorkplaceSearchChange}
                      loading={workplaceSearchLoading}
                      placeholder="Busque por uma cidade"
                      searchPlaceholder="Digite o nome da cidade"
                      emptyMessage="Nenhuma cidade encontrada"
                      testId="job-conditions-workplace"
                      error={Boolean(errors.workplaceCities)}
                    />
                    <FormFieldError className="text-sm leading-5 text-muted-foreground">
                      {errors.workplaceCities}
                    </FormFieldError>
                  </FormField>
                </div>
              </BlockedEditTooltip>

              <BlockedEditTooltip enabled={readOnly}>
                <FormField error={Boolean(errors.relocationAvailability)}>
                  <FormFieldLabel className="text-foreground">
                    Disponibilidade para mudança
                  </FormFieldLabel>
                  {isOptional('relocationAvailability') ? (
                    <FormFieldHint>{OPTIONAL_FIELD_HINT}</FormFieldHint>
                  ) : null}
                  <RadioCardGroup
                    value={values.relocationAvailability}
                    onValueChange={(value) =>
                      onFieldChange('relocationAvailability', value as YesNo)
                    }
                    className="flex-wrap"
                  >
                    {YES_NO_OPTIONS.map((option) => (
                      <RadioCardItem
                        key={option.value}
                        value={option.value}
                        className="group px-4 py-4"
                        data-testid={`job-conditions-relocation-${option.value}`}
                      >
                        {option.label}
                      </RadioCardItem>
                    ))}
                  </RadioCardGroup>
                  <FormFieldError className="text-sm leading-5 text-muted-foreground">
                    {errors.relocationAvailability}
                  </FormFieldError>
                </FormField>
              </BlockedEditTooltip>

              {showRelocationScope ? (
                <BlockedEditTooltip enabled={readOnly}>
                  <FormField error={Boolean(errors.relocationScope)}>
                    <FormFieldLabel className="text-foreground">Local da mudança</FormFieldLabel>
                    {isOptional('relocationScopes') ? (
                      <FormFieldHint>{OPTIONAL_FIELD_HINT}</FormFieldHint>
                    ) : null}
                    <CheckboxPillGroup
                      options={SCOPE_OPTIONS}
                      selected={values.relocationScope}
                      onToggle={(value) =>
                        onFieldChange('relocationScope', toggleScope(values.relocationScope, value))
                      }
                      testIdPrefix="job-conditions-relocation-scope"
                    />
                    <FormFieldError className="text-sm leading-5 text-muted-foreground">
                      {errors.relocationScope}
                    </FormFieldError>
                  </FormField>
                </BlockedEditTooltip>
              ) : null}

              <BlockedEditTooltip enabled={readOnly}>
                <FormField error={Boolean(errors.travelAvailability)}>
                  <FormFieldLabel className="text-foreground">
                    Disponibilidade para viagens
                  </FormFieldLabel>
                  {isOptional('travelAvailability') ? (
                    <FormFieldHint>{OPTIONAL_FIELD_HINT}</FormFieldHint>
                  ) : null}
                  <RadioCardGroup
                    value={values.travelAvailability}
                    onValueChange={(value) => onFieldChange('travelAvailability', value as YesNo)}
                    className="flex-wrap"
                  >
                    {YES_NO_OPTIONS.map((option) => (
                      <RadioCardItem
                        key={option.value}
                        value={option.value}
                        className="group px-4 py-4"
                        data-testid={`job-conditions-travel-${option.value}`}
                      >
                        {option.label}
                      </RadioCardItem>
                    ))}
                  </RadioCardGroup>
                  <FormFieldError className="text-sm leading-5 text-muted-foreground">
                    {errors.travelAvailability}
                  </FormFieldError>
                </FormField>
              </BlockedEditTooltip>

              {showTravelScope ? (
                <BlockedEditTooltip enabled={readOnly}>
                  <FormField error={Boolean(errors.travelScope)}>
                    <FormFieldLabel className="text-foreground">Destino das viagens</FormFieldLabel>
                    {isOptional('travelScopes') ? (
                      <FormFieldHint>{OPTIONAL_FIELD_HINT}</FormFieldHint>
                    ) : null}
                    <CheckboxPillGroup
                      options={SCOPE_OPTIONS}
                      selected={values.travelScope}
                      onToggle={(value) =>
                        onFieldChange('travelScope', toggleScope(values.travelScope, value))
                      }
                      testIdPrefix="job-conditions-travel-scope"
                    />
                    <FormFieldError className="text-sm leading-5 text-muted-foreground">
                      {errors.travelScope}
                    </FormFieldError>
                  </FormField>
                </BlockedEditTooltip>
              ) : null}

              {/* EU-497 — Turno múltipla escolha (RN-02), inclui Integral. */}
              <BlockedEditTooltip enabled={readOnly}>
                <FormField error={Boolean(errors.shifts)}>
                  <FormFieldLabel className="text-foreground">Turno de trabalho</FormFieldLabel>
                  <FormFieldHint>
                    {isOptional('shifts') ? OPTIONAL_FIELD_HINT : 'Selecione um ou mais turnos'}
                  </FormFieldHint>
                  <CheckboxPillGroup
                    options={SHIFT_OPTIONS}
                    selected={values.shifts}
                    onToggle={(value) => onFieldChange('shifts', toggleValue(values.shifts, value))}
                    testIdPrefix="job-conditions-shift"
                  />
                  <FormFieldError className="text-sm leading-5 text-muted-foreground">
                    {errors.shifts}
                  </FormFieldError>
                </FormField>
              </BlockedEditTooltip>

              {/* EU-497 — Carga horária múltipla escolha (RN-03). */}
              <BlockedEditTooltip enabled={readOnly}>
                <FormField error={Boolean(errors.weeklyHoursList)}>
                  <FormFieldLabel className="text-foreground">Carga horária semanal</FormFieldLabel>
                  <FormFieldHint>
                    {isOptional('weeklyHoursList')
                      ? OPTIONAL_FIELD_HINT
                      : 'Selecione uma ou mais opções'}
                  </FormFieldHint>
                  <CheckboxPillGroup
                    options={WEEKLY_HOURS_OPTIONS}
                    selected={values.weeklyHoursList}
                    onToggle={(value) =>
                      onFieldChange('weeklyHoursList', toggleValue(values.weeklyHoursList, value))
                    }
                    testIdPrefix="job-conditions-weekly"
                  />
                  <FormFieldError className="text-sm leading-5 text-muted-foreground">
                    {errors.weeklyHoursList}
                  </FormFieldError>
                </FormField>
              </BlockedEditTooltip>
            </fieldset>

            <div className="mt-auto flex flex-row justify-between gap-2 pt-8">
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={onBack}
                data-testid="job-conditions-back"
              >
                Voltar
              </Button>
              {readOnly ? (
                <Button type="button" size="lg" disabled data-testid="job-edit-save-blocked">
                  Salvar alterações
                </Button>
              ) : (
                <Button
                  type="submit"
                  size="lg"
                  loading={isSubmitting}
                  data-testid="job-conditions-submit"
                >
                  Salvar alterações
                </Button>
              )}
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}

function toggleScope(current: RelocationScope[], value: RelocationScope): RelocationScope[] {
  if (current.includes(value)) {
    return current.filter((entry) => entry !== value);
  }
  return [...current, value];
}

/** EU-497 — toggle genérico de string em array (Modelo/Turno/Carga multi). */
function toggleValue(current: string[], value: string): string[] {
  if (current.includes(value)) {
    return current.filter((entry) => entry !== value);
  }
  return [...current, value];
}

interface MultiSelectComboboxWithChipsProps {
  options: ComboboxOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  testId: string;
  error?: boolean;
  disabled?: boolean;
  /** EU-497 RN-04 — busca server-side: reporta o termo digitado + estado de load. */
  onSearchChange?: (query: string) => void;
  loading?: boolean;
}

/**
 * Juliana QA bug — EU-160. Multi-select sob o `Combobox` single existente
 * (sem inflar o design system com nova primitiva): combobox de seleção
 * individual atualiza array (toggle); chips removíveis abaixo via `Badge`
 * + `XIcon`. Sem options "selecionar todos" — usuário pode escolher uma a
 * uma. Quando o option já está selecionado, clicar de novo no combobox
 * remove (toggle).
 */
function MultiSelectComboboxWithChips({
  options,
  selected,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  testId,
  error,
  disabled,
  onSearchChange,
  loading,
}: MultiSelectComboboxWithChipsProps) {
  const optionsByValue = new Map(options.map((o) => [o.value, o]));
  function handleSelect(value: string) {
    if (selected.includes(value)) {
      onChange(selected.filter((s) => s !== value));
    } else {
      onChange([...selected, value]);
    }
  }
  function handleRemove(value: string) {
    onChange(selected.filter((s) => s !== value));
  }
  return (
    <div className="flex flex-col gap-2">
      <Combobox
        options={options}
        // Single-Combobox quer um value escalar; passamos sempre `undefined`
        // para o trigger mostrar o placeholder permanente e a seleção viver
        // só nos chips.
        value={undefined}
        onSelect={handleSelect}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        error={error}
        disabled={disabled}
        onSearchChange={onSearchChange}
        loading={loading}
        data-testid={testId}
      />
      {selected.length > 0 ? (
        <div className="flex flex-row flex-wrap gap-2" data-testid={`${testId}-chips`}>
          {selected.map((value) => {
            const label = optionsByValue.get(value)?.label ?? value;
            return (
              <Badge
                key={value}
                variant="secondary"
                shape="pill"
                className="gap-1.5 py-1 pl-2.5 pr-1"
                data-testid={`${testId}-chip-${value}`}
              >
                <span>{label}</span>
                <button
                  type="button"
                  aria-label={`Remover ${label}`}
                  onClick={() => handleRemove(value)}
                  className="inline-flex items-center justify-center rounded-full p-0.5 hover:bg-secondary-foreground/10"
                  data-testid={`${testId}-chip-${value}-remove`}
                >
                  <XIcon className="size-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

interface CheckboxPillGroupProps<T extends string> {
  options: readonly { value: T; label: string }[];
  // EU-497 — `readonly string[]` para aceitar tanto os arrays de escopo
  // (RelocationScope[]) quanto os de Modelo/Turno/Carga (string[]).
  selected: readonly string[];
  onToggle: (value: T) => void;
  testIdPrefix: string;
}

function CheckboxPillGroup<T extends string>({
  options,
  selected,
  onToggle,
  testIdPrefix,
}: CheckboxPillGroupProps<T>) {
  return (
    <div className="flex flex-row flex-wrap gap-2">
      {options.map((option) => {
        const isChecked = selected.includes(option.value);
        return (
          <label
            key={option.value}
            className={cn(
              'group inline-flex cursor-pointer select-none items-center gap-2 rounded-xl border bg-card px-4 py-3 text-sm font-medium transition-colors',
              isChecked
                ? 'border-primary bg-primary/10 text-foreground'
                : 'border-border text-foreground hover:border-primary'
            )}
          >
            <Checkbox
              checked={isChecked}
              onCheckedChange={() => onToggle(option.value)}
              className="size-4"
              data-testid={`${testIdPrefix}-${option.value}`}
            />
            <span>{option.label}</span>
          </label>
        );
      })}
    </div>
  );
}
